import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number | undefined} */
  #reservationInterval;

  /** @type {number | undefined} */
  #reservationStartTimeout;

  /** @type {number} */
  #reservationDuration = 300;

  /** @type {number} */
  #reservationRemaining = 300;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#startReservationTimer);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#startReservationTimer);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    this.#clearReservationTimer();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (!event.detail.data?.didError) {
      this.showDialog();
      this.#startReservationTimer();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();
    this.#startReservationTimer();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #clearReservationTimer() {
    if (this.#reservationInterval) {
      clearInterval(this.#reservationInterval);
      this.#reservationInterval = undefined;
    }

    if (this.#reservationStartTimeout) {
      clearTimeout(this.#reservationStartTimeout);
      this.#reservationStartTimeout = undefined;
    }
  }

  #startReservationTimer = () => {
    this.#clearReservationTimer();

    this.#reservationStartTimeout = setTimeout(() => {
      const timerRoot = /** @type {HTMLElement | null} */ (this.querySelector('cart-reservation-timer'));
      if (!timerRoot) return;

      this.#reservationDuration = (Number(timerRoot.dataset.minutes) || 5) * 60;
      this.#reservationRemaining = this.#reservationDuration;
      timerRoot.removeAttribute('data-expired');
      this.#renderReservationTimer();

      this.#reservationInterval = setInterval(() => {
        this.#reservationRemaining = Math.max(0, this.#reservationRemaining - 1);
        this.#renderReservationTimer();

        if (this.#reservationRemaining === 0) {
          if (this.#reservationInterval) {
            clearInterval(this.#reservationInterval);
            this.#reservationInterval = undefined;
          }
        }
      }, 1000);
    }, 80);
  };

  #renderReservationTimer() {
    const timerRoot = /** @type {HTMLElement | null} */ (this.querySelector('cart-reservation-timer'));
    const timeElement = timerRoot?.querySelector('[data-cart-reservation-time]');
    const progressElement = /** @type {HTMLElement | null | undefined} */ (
      timerRoot?.querySelector('[data-cart-reservation-progress]')
    );
    const messageElement = timerRoot?.querySelector('[data-cart-reservation-message]');

    if (!timerRoot || !timeElement) return;

    const minutes = Math.floor(this.#reservationRemaining / 60)
      .toString()
      .padStart(2, '0');
    const seconds = (this.#reservationRemaining % 60).toString().padStart(2, '0');
    timeElement.textContent = `${minutes}:${seconds}`;

    if (progressElement) {
      progressElement.style.inlineSize = `${(this.#reservationRemaining / this.#reservationDuration) * 100}%`;
    }

    if (this.#reservationRemaining === 0 && messageElement) {
      timerRoot.dataset.expired = 'true';
      messageElement.textContent = 'Reservation expiree. Validez maintenant pour confirmer la disponibilite.';
    }
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
